import { FC, ReactElement, ReactNode } from 'react';
import i18next from 'i18next';
import { EnduranceTrainingTypeDto } from '../../../../shared/dtos/enduranceTrainingType.dto';
import { PromMetric } from '../../../../shared/utils/enums';
import DataTable, { DataTableColumn } from '../data-table/DataTable';
import StatusBadge from '../data-table/StatusBadge';

type EnduranceTrainingTypeTableProps = {
  types: EnduranceTrainingTypeDto[];
  isLoading: boolean;
  toolbar?: ReactNode;
  onClick?: (type: EnduranceTrainingTypeDto) => void;
}

const EnduranceTrainingTypeTable: FC<EnduranceTrainingTypeTableProps> = ({
  types,
  isLoading,
  toolbar,
  onClick,
}: EnduranceTrainingTypeTableProps): ReactElement => {
  const renderExcludedPromMetrics = (type: EnduranceTrainingTypeDto): string => {
    const metrics: PromMetric[] = type.excludedPromMetrics ?? [];

    if (metrics.length === 0) {
      return '-';
    }

    return metrics.map((metric: PromMetric) => i18next.t(`enduranceTrainingType.promMetric.${metric}`)).join(', ');
  };

  const columns: DataTableColumn<EnduranceTrainingTypeDto>[] = [
    {
      key: 'deName',
      header: i18next.t('enduranceTrainingType.deName'),
      cell: (type: EnduranceTrainingTypeDto) => <span className="fw-semibold">{type.deName}</span>,
    },
    {
      key: 'enName',
      header: i18next.t('enduranceTrainingType.enName'),
      cell: (type: EnduranceTrainingTypeDto) => <span className="text-muted">{type.enName}</span>,
    },
    {
      key: 'trainingMinutes',
      header: i18next.t('enduranceTrainingType.trainingMinutes'),
      align: 'end',
      cell: (type: EnduranceTrainingTypeDto) => type.trainingMinutes,
    },
    {
      key: 'isBillable',
      header: i18next.t('enduranceTrainingType.isBillable'),
      cell: (type: EnduranceTrainingTypeDto) => (
        <StatusBadge
          isActive={type.isBillable}
          label={i18next.t(type.isBillable ? 'enduranceTrainingType.billable' : 'enduranceTrainingType.notBillable')}
        />
      ),
    },
    {
      key: 'unit',
      header: i18next.t('enduranceTrainingType.unit'),
      cell: (type: EnduranceTrainingTypeDto) => `${type.unit?.deName ?? '-'} (${type.unit?.deUnit ?? '-'})`,
    },
    {
      key: 'excludedPromMetrics',
      header: i18next.t('enduranceTrainingType.excludedPromMetrics'),
      cell: renderExcludedPromMetrics,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={types}
      rowKey={(type: EnduranceTrainingTypeDto) => type.id}
      onRowClick={onClick}
      isLoading={isLoading}
      toolbar={toolbar}
    />
  );
};

export default EnduranceTrainingTypeTable;
